// pipe: the "Customise navigation" sheet — reorder and hide the ERP sidebar sections with
// buttons (↑ ↓ hide/show), the keyboard (Alt+↑/↓ on a focused row, Space/Enter toggles hide) and
// Reset. It is the ONE arrange surface for touch, keyboard and anyone who prefers buttons to
// dragging; desktop drag-to-reorder (erp-nav-list.tsx) saves through the same pure model.
import { Button, IconButton, Modal, useConfirmModal } from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  ArrowDownSmallIcon,
  ArrowUpSmallIcon,
  InvisibleIcon,
  ViewIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ErpService } from '../../modules/erp';
import {
  applyNavLayout,
  DEFAULT_NAV_ORDER,
  moveNav,
  type NavId,
  type NavLayout,
  toggleHidden,
} from '../../modules/erp/nav-layout';
import { KEY_NAV_LAYOUT } from '../../modules/erp/user-preferences';
import * as styles from './customise-nav-sheet.css';

const SECTION_LABEL_KEYS = {
  dashboard: 'com.affine.pipe.nav.section.dashboard',
  'all-docs': 'com.affine.pipe.nav.section.all-docs',
  records: 'com.affine.pipe.nav.section.records',
  ops: 'com.affine.pipe.nav.section.ops',
  intake: 'com.affine.pipe.nav.section.intake',
  customers: 'com.affine.pipe.nav.section.customers',
  sites: 'com.affine.pipe.nav.section.sites',
  scan: 'com.affine.pipe.nav.section.scan',
  work: 'com.affine.pipe.nav.section.work',
  director: 'com.affine.pipe.nav.section.director',
  qms: 'com.affine.pipe.nav.section.qms',
  refining: 'com.affine.pipe.nav.section.refining',
  sales: 'com.affine.pipe.nav.section.sales',
  listings: 'com.affine.pipe.nav.section.listings',
  comms: 'com.affine.pipe.nav.section.comms',
  agreements: 'com.affine.pipe.nav.section.agreements',
  inbound: 'com.affine.pipe.nav.section.inbound',
  finance: 'com.affine.pipe.nav.section.finance',
  reports: 'com.affine.pipe.nav.section.reports',
  ask: 'com.affine.pipe.nav.section.ask',
  journal: 'com.affine.pipe.nav.section.journal',
  notifications: 'com.affine.pipe.nav.section.notifications',
} as const satisfies Record<NavId, string>;

export interface CustomiseNavSheetProps {
  open: boolean;
  onClose: () => void;
  /** The row to focus when the sheet opens from a press-and-hold on that section. */
  initialFocusId?: NavId;
}

export function CustomiseNavSheet({
  open,
  onClose,
  initialFocusId,
}: CustomiseNavSheetProps) {
  const t = useI18n();
  const erp = useService(ErpService);
  const state = useLiveData(erp.preferences.pref<NavLayout>(KEY_NAV_LAYOUT));
  const layout = state.value;
  const { visible, hidden } = applyNavLayout(DEFAULT_NAV_ORDER, layout);
  const [error, setError] = useState<string | null>(null);
  const { openConfirmModal } = useConfirmModal();
  const rowRefs = useRef(new Map<NavId, HTMLLIElement>());

  const persist = useCallback(
    (next: NavLayout) => {
      setError(null);
      erp.preferences
        .save(KEY_NAV_LAYOUT, next)
        .catch(() => setError(t['com.affine.pipe.nav.customise.save-failed']()));
    },
    [erp, t]
  );
  const move = useCallback(
    (id: NavId, dir: -1 | 1) => {
      persist(moveNav(layout, id, dir, DEFAULT_NAV_ORDER));
      // keep the keyboard on the moved row
      requestAnimationFrame(() => rowRefs.current.get(id)?.focus());
    },
    [layout, persist]
  );
  const toggle = useCallback(
    (id: NavId) => persist(toggleHidden(layout, id, DEFAULT_NAV_ORDER)),
    [layout, persist]
  );
  const onReset = useCallback(() => {
    openConfirmModal({
      title: t['com.affine.pipe.nav.customise.reset'](),
      description: t['com.affine.pipe.nav.customise.reset-confirm'](),
      cancelText: t['Cancel'](),
      confirmText: t['com.affine.pipe.nav.customise.reset'](),
      onConfirm: () => {
        setError(null);
        erp.preferences
          .reset(KEY_NAV_LAYOUT)
          .catch(() =>
            setError(t['com.affine.pipe.nav.customise.save-failed']())
          );
      },
    });
  }, [erp, openConfirmModal, t]);

  useEffect(() => {
    if (open && initialFocusId) {
      requestAnimationFrame(() =>
        rowRefs.current.get(initialFocusId)?.focus()
      );
    }
  }, [open, initialFocusId]);

  const name = (id: NavId) => t[SECTION_LABEL_KEYS[id]]();

  const renderRow = (id: NavId, isHidden: boolean) => (
    <li
      key={id}
      data-nav-id={id}
      data-hidden={isHidden}
      className={styles.row}
      tabIndex={0}
      ref={el => {
        if (el) rowRefs.current.set(id, el);
        else rowRefs.current.delete(id);
      }}
      onKeyDown={e => {
        if (e.altKey && e.key === 'ArrowUp') {
          e.preventDefault();
          move(id, -1);
        } else if (e.altKey && e.key === 'ArrowDown') {
          e.preventDefault();
          move(id, 1);
        } else if (
          (e.key === ' ' || e.key === 'Enter') &&
          e.target === e.currentTarget
        ) {
          e.preventDefault();
          toggle(id);
        }
      }}
    >
      <span className={styles.label}>{name(id)}</span>
      {!isHidden && (
        <>
          <IconButton
            size="16"
            icon={<ArrowUpSmallIcon />}
            aria-label={t['com.affine.pipe.nav.customise.up']({ name: name(id) })}
            onClick={() => move(id, -1)}
          />
          <IconButton
            size="16"
            icon={<ArrowDownSmallIcon />}
            aria-label={t['com.affine.pipe.nav.customise.down']({
              name: name(id),
            })}
            onClick={() => move(id, 1)}
          />
        </>
      )}
      <IconButton
        size="16"
        icon={isHidden ? <ViewIcon /> : <InvisibleIcon />}
        aria-label={
          isHidden
            ? t['com.affine.pipe.nav.customise.show']({ name: name(id) })
            : t['com.affine.pipe.nav.customise.hide']({ name: name(id) })
        }
        onClick={() => toggle(id)}
      />
    </li>
  );

  return (
    <Modal
      open={open}
      onOpenChange={o => {
        if (!o) onClose();
      }}
      title={t['com.affine.pipe.nav.customise.title']()}
      width={420}
    >
      <p className={styles.hint}>{t['com.affine.pipe.nav.customise.hint']()}</p>
      <ul className={styles.list} aria-label={t['com.affine.pipe.nav.customise.title']()}>
        {visible.map(id => renderRow(id, false))}
      </ul>
      {hidden.length > 0 && (
        <>
          <div className={styles.section}>
            {t['com.affine.pipe.nav.customise.hidden-section']()}
          </div>
          <ul className={styles.list}>{hidden.map(id => renderRow(id, true))}</ul>
        </>
      )}
      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}
      <div className={styles.actions}>
        <Button variant="secondary" onClick={onReset}>
          {t['com.affine.pipe.nav.customise.reset']()}
        </Button>
        <Button variant="primary" onClick={onClose}>
          {t['com.affine.pipe.nav.customise.done']()}
        </Button>
      </div>
    </Modal>
  );
}
